import { useState, useEffect, useCallback } from "react";
import { C } from "../data";
import { useAuth } from "../context/AuthContext";
import {
  fetchReviewStats, fetchReviews, fetchMyHelpfulVotes,
  createReview, toggleHelpful, deleteReview, checkVerifiedPurchase,
  fetchReviewComments, checkHasReviewed,
} from "../api";

const PAGE_SIZE = 5;
const SORTS = [
  ["latest",  "최신순"],
  ["highest", "평점 높은순"],
  ["lowest",  "평점 낮은순"],
  ["helpful", "도움순"],
];
const REVIEW_TYPES = [
  { value: "text",          label: "텍스트 리뷰",   points: 1000 },
  { value: "photo",         label: "포토 리뷰",     points: 2000 },
  { value: "wearing_photo", label: "착용 포토 리뷰", points: 3000 },
];

function maskName(raw) {
  const name = (raw || "").trim() || "익명";
  if (name.length <= 1) return name;
  if (name.length === 2) return name[0] + "*";
  return name[0] + "*".repeat(name.length - 2) + name[name.length - 1];
}

export function Stars({ value, size = 14, onSelect }) {
  const interactive = typeof onSelect === "function";
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onClick={interactive ? () => onSelect(n) : undefined}
          style={{
            fontSize: size, lineHeight: 1, color: n <= Math.round(value) ? "#E8A838" : "#E0E0DE",
            cursor: interactive ? "pointer" : "default",
          }}
        >★</span>
      ))}
    </span>
  );
}

export default function ProductReviews({ productId }) {
  const { user, profile } = useAuth();

  const [stats, setStats] = useState({ avg_rating: 0, total_count: 0, r5: 0, r4: 0, r3: 0, r2: 0, r1: 0 });
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("latest");
  const [loading, setLoading] = useState(true);
  const [myVotes, setMyVotes] = useState([]);
  const [verifiedPurchase, setVerifiedPurchase] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [commentsByReview, setCommentsByReview] = useState({});

  const [showWrite, setShowWrite] = useState(false);
  const [wRating, setWRating] = useState(5);
  const [wTitle, setWTitle] = useState("");
  const [wContent, setWContent] = useState("");
  const [wImages, setWImages] = useState([]);
  const [wReviewType, setWReviewType] = useState("text");
  const [wSaving, setWSaving] = useState(false);
  const [wError, setWError] = useState("");
  const [submitNotice, setSubmitNotice] = useState("");

  const loadStats = useCallback(() => {
    fetchReviewStats(productId).then(setStats).catch(() => {});
  }, [productId]);

  const loadReviews = useCallback(async (nextPage, nextSort, append) => {
    setLoading(true);
    try {
      const { reviews: data, total: t } = await fetchReviews(productId, { sort: nextSort, page: nextPage, pageSize: PAGE_SIZE });
      setTotal(t);
      setReviews(prev => append ? [...prev, ...data] : data);
      const voteIds = await fetchMyHelpfulVotes(user?.id, data.map(r => r.id)).catch(() => []);
      setMyVotes(prev => append ? [...new Set([...prev, ...voteIds])] : voteIds);

      const ids = data.map(r => r.id);
      const comments = await fetchReviewComments(ids).catch(() => []);
      const map = {};
      ids.forEach(id => { map[id] = []; });
      comments.forEach(c => { if (map[c.review_id]) map[c.review_id].push(c); });
      setCommentsByReview(prev => append ? { ...prev, ...map } : map);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [productId, user?.id]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPage(0); loadReviews(0, sort, false); }, [sort, loadReviews]);

  useEffect(() => {
    if (user?.id) {
      checkVerifiedPurchase(user.id, productId).then(setVerifiedPurchase).catch(() => setVerifiedPurchase(false));
      checkHasReviewed(user.id, productId).then(setHasReviewed).catch(() => setHasReviewed(false));
    } else {
      setVerifiedPurchase(false);
      setHasReviewed(false);
    }
  }, [user?.id, productId]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    loadReviews(next, sort, true);
  };

  const handleVote = async (review) => {
    if (!user) { alert("로그인이 필요합니다."); return; }
    const voted = myVotes.includes(review.id);
    setMyVotes(prev => voted ? prev.filter(id => id !== review.id) : [...prev, review.id]);
    setReviews(prev => prev.map(r => r.id === review.id ? { ...r, helpful_count: r.helpful_count + (voted ? -1 : 1) } : r));
    try {
      await toggleHelpful(review.id, user.id, voted);
    } catch {
      setMyVotes(prev => voted ? [...prev, review.id] : prev.filter(id => id !== review.id));
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, helpful_count: r.helpful_count + (voted ? 1 : -1) } : r));
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm("리뷰를 삭제하시겠습니까?")) return;
    await deleteReview(review.id);
    setReviews(prev => prev.filter(r => r.id !== review.id));
    setHasReviewed(false);
    loadStats();
  };

  const handleImagePick = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - wImages.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setWImages(prev => [...prev, ev.target.result].slice(0, 4));
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (idx) => setWImages(prev => prev.filter((_, i) => i !== idx));

  const resetWriteForm = () => {
    setWRating(5); setWTitle(""); setWContent(""); setWImages([]); setWReviewType("text"); setWError("");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!wContent.trim()) { setWError("리뷰 내용을 입력해 주세요."); return; }
    setWSaving(true); setWError("");
    try {
      await createReview({
        productId, userId: user.id, rating: wRating,
        title: wTitle.trim(), content: wContent.trim(), images: wImages,
        reviewType: wReviewType,
      });
      const type = wReviewType;
      resetWriteForm();
      setShowWrite(false);
      setHasReviewed(true);
      setSort("latest");
      loadStats();
      loadReviews(0, "latest", false);
      setPage(0);
      if (type !== "text") {
        const total = type === "wearing_photo" ? "₩3,000" : "₩2,000";
        setSubmitNotice(`₩1,000이 즉시 적립됐습니다. 관리자 포토 리뷰 확인 후 나머지 적립금이 지급되어 총 ${total}이 적립됩니다.`);
        setTimeout(() => setSubmitNotice(""), 10000);
      }
    } catch (err) {
      setWError("등록 중 오류가 발생했습니다: " + err.message);
    } finally {
      setWSaving(false);
    }
  };

  const maxBarCount = Math.max(stats.r5, stats.r4, stats.r3, stats.r2, stats.r1, 1);

  return (
    <section id="product-reviews" style={{ marginTop: 96, paddingTop: 64, borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 36 }}>
        <h3 style={{ fontFamily: "'DM Serif Display',serif", fontWeight: 400, fontSize: 28 }}>Reviews</h3>
        {user && verifiedPurchase && !hasReviewed && (
          <button
            onClick={() => setShowWrite(true)}
            style={{
              fontFamily: "'Presentation5',sans-serif",
              fontSize: 9,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: C.bg,
              background: C.ink,
              border: "none",
              padding: "10px 20px",
              cursor: "pointer",
              transition: "opacity .18s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = ".75"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Write a Review
          </button>
        )}
        {user && verifiedPurchase && hasReviewed && (
          <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 10, letterSpacing: 1, color: C.muted }}>
            리뷰를 작성하셨습니다
          </span>
        )}
      </div>

      {submitNotice && (
        <div style={{ marginBottom: 20, padding: "12px 16px", background: "#E8F5EC", border: "1px solid #B2D8C5", fontFamily: "'Presentation5',sans-serif", fontSize: 11, color: "#2E7D52", lineHeight: 1.6 }}>
          {submitNotice}
        </div>
      )}

      {/* 요약 */}
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 40, marginBottom: 48, alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontFamily: "'Presentation5',serif", fontSize: 40, lineHeight: 1, marginBottom: 8 }}>{stats.avg_rating || 0}</p>
          <Stars value={stats.avg_rating} size={16} />
          <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 11, color: C.muted, marginTop: 8 }}>{stats.total_count}개 리뷰</p>
        </div>
        <div>
          {[5, 4, 3, 2, 1].map(n => {
            const cnt = stats[`r${n}`] || 0;
            return (
              <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 11, color: C.muted, width: 32 }}>{n}점</span>
                <div style={{ flex: 1, height: 6, background: C.bg2, position: "relative" }}>
                  <div style={{ position: "absolute", inset: 0, width: `${(cnt / maxBarCount) * 100}%`, background: "#E8A838" }} />
                </div>
                <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 11, color: C.muted, width: 24, textAlign: "right" }}>{cnt}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 정렬 */}
      {stats.total_count > 0 && (
        <div style={{ display: "flex", gap: 16, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
          {SORTS.map(([key, label]) => (
            <button key={key} onClick={() => setSort(key)} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: "'Presentation5',sans-serif", fontSize: 11, letterSpacing: 1,
              color: sort === key ? C.ink : C.muted, fontWeight: sort === key ? 600 : 400,
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* 목록 */}
      {!loading && reviews.length === 0 && (
        <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 13, color: C.muted, padding: "40px 0", textAlign: "center" }}>
          아직 작성된 리뷰가 없습니다. 첫 리뷰를 남겨보세요.
        </p>
      )}

      {reviews.map(r => {
        const reviewerName = maskName(r.profiles?.name || r.profiles?.username);
        const typeInfo = REVIEW_TYPES.find(t => t.value === r.review_type) || REVIEW_TYPES[0];
        return (
          <div key={r.id} style={{ padding: "24px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <Stars value={r.rating} />
                  {r.is_best && (
                    <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 9, letterSpacing: 1.5, color: "#C5A028", border: "1px solid #C5A028", padding: "2px 6px", borderRadius: 2 }}>BEST</span>
                  )}
                  {r.verified_purchase && (
                    <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 9, letterSpacing: 1, color: "#3D8B5C", border: "1px solid #3D8B5C", padding: "2px 6px", borderRadius: 2 }}>구매 확인</span>
                  )}
                  <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 9, color: C.muted, border: `1px solid ${C.border}`, padding: "2px 6px", borderRadius: 2 }}>{typeInfo.label}</span>
                </div>
                <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 11, color: C.muted }}>
                  {reviewerName} · {new Date(r.created_at).toLocaleDateString("ko-KR")}
                </p>
              </div>
              {user?.id === r.user_id && (
                <button onClick={() => handleDelete(r)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: C.muted }}>삭제</button>
              )}
            </div>
            {r.title && <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{r.title}</p>}
            <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 13, color: C.ink, lineHeight: 1.8, whiteSpace: "pre-line", marginBottom: r.images?.length ? 12 : 10 }}>{r.content}</p>
            {r.images?.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                {r.images.map((img, i) => (
                  <img key={i} src={img} alt="" style={{ width: 72, height: 72, objectFit: "cover", cursor: "pointer" }}
                    onClick={() => window.open(img, "_blank")} />
                ))}
              </div>
            )}
            <button onClick={() => handleVote(r)} style={{
              background: "none", border: `1px solid ${myVotes.includes(r.id) ? C.ink : C.border}`, cursor: "pointer",
              padding: "5px 12px", fontFamily: "'Presentation5',sans-serif", fontSize: 10, letterSpacing: 1,
              color: myVotes.includes(r.id) ? C.ink : C.muted,
            }}>도움이 됐어요 ({r.helpful_count})</button>

            {(commentsByReview[r.id] || []).map(c => (
              <div key={c.id} style={{ marginTop: 12, background: "#F7F5F2", borderLeft: `3px solid ${C.accent}`, padding: "12px 16px" }}>
                <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 9, letterSpacing: 2, color: C.accent, marginBottom: 6 }}>UNDERLOOKED</p>
                <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 12, color: C.ink, lineHeight: 1.8, whiteSpace: "pre-line" }}>{c.content}</p>
                <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 10, color: C.muted, marginTop: 6 }}>{new Date(c.created_at).toLocaleDateString("ko-KR")}</p>
              </div>
            ))}
          </div>
        );
      })}

      {reviews.length < total && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <button onClick={loadMore} disabled={loading} style={{
            background: "none", border: `1px solid ${C.border}`, padding: "10px 28px", cursor: "pointer",
            fontFamily: "'Presentation5',sans-serif", fontSize: 11, letterSpacing: 1, color: C.ink, opacity: loading ? .6 : 1,
          }}>{loading ? "불러오는 중..." : "리뷰 더보기"}</button>
        </div>
      )}

      {/* 리뷰 작성 모달 */}
      {showWrite && (
        <div style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(26,26,26,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setShowWrite(false)}>
          <div style={{ background: C.bg, width: "100%", maxWidth: 460, padding: "32px", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <p style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, marginBottom: 24, textAlign: "center" }}>리뷰 작성</p>
            <form onSubmit={submitReview} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ textAlign: "center" }}>
                <Stars value={wRating} size={28} onSelect={setWRating} />
              </div>

              {/* 리뷰 유형 선택 */}
              <div>
                <label className="adm-label">리뷰 유형</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {REVIEW_TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => setWReviewType(t.value)}
                      style={{
                        flex: 1, padding: "10px 8px", border: `1px solid ${wReviewType === t.value ? C.ink : C.border}`,
                        background: wReviewType === t.value ? C.ink : "transparent",
                        color: wReviewType === t.value ? C.bg : C.ink,
                        fontFamily: "'Presentation5',sans-serif", fontSize: 10, letterSpacing: 0.5, cursor: "pointer",
                        textAlign: "center",
                      }}>
                      <div>{t.label}</div>
                      <div style={{ marginTop: 4, color: wReviewType === t.value ? "rgba(255,255,255,.7)" : C.muted, fontSize: 9 }}>
                        +{t.points.toLocaleString()}원 적립
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="adm-label">제목 (선택)</label>
                <input className="adm-input" value={wTitle} onChange={e => setWTitle(e.target.value)} placeholder="한줄로 정리해 주세요" />
              </div>
              <div>
                <label className="adm-label">내용</label>
                <textarea className="adm-input" rows={5} style={{ resize: "vertical" }} value={wContent}
                  onChange={e => setWContent(e.target.value)} placeholder={`착용감, 사이즈, 소재감 등을 자유롭게 ${profile?.name ? profile.name + '님의 ' : ''}경험으로 남겨주세요`} required />
              </div>

              {(wReviewType === "photo" || wReviewType === "wearing_photo") && (
                <div>
                  <label className="adm-label">사진 (최대 4장)</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {wImages.map((img, i) => (
                      <div key={i} style={{ position: "relative", width: 64, height: 64 }}>
                        <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button" onClick={() => removeImage(i)} style={{
                          position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
                          background: C.ink, color: "#fff", border: "none", fontSize: 10, cursor: "pointer", lineHeight: 1,
                        }}>✕</button>
                      </div>
                    ))}
                    {wImages.length < 4 && (
                      <label style={{ width: 64, height: 64, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.muted, fontSize: 20 }}>
                        +
                        <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImagePick} />
                      </label>
                    )}
                  </div>
                </div>
              )}

              {wReviewType === "text" && (
                <div style={{ marginTop: -4 }}>
                  <label style={{ display: "block", fontFamily: "'Presentation5',sans-serif", fontSize: 10, letterSpacing: 1, color: C.muted, marginBottom: 6 }}>사진 (선택, 최대 4장)</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {wImages.map((img, i) => (
                      <div key={i} style={{ position: "relative", width: 64, height: 64 }}>
                        <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button" onClick={() => removeImage(i)} style={{
                          position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
                          background: C.ink, color: "#fff", border: "none", fontSize: 10, cursor: "pointer", lineHeight: 1,
                        }}>✕</button>
                      </div>
                    ))}
                    {wImages.length < 4 && (
                      <label style={{ width: 64, height: 64, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.muted, fontSize: 20 }}>
                        +
                        <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImagePick} />
                      </label>
                    )}
                  </div>
                </div>
              )}

              {wError && <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: "#C94040", textAlign: "center" }}>{wError}</p>}
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowWrite(false)} style={{ flex: 1, padding: "13px 0", background: "none", border: `1px solid ${C.border}`, cursor: "pointer", fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2 }}>취소</button>
                <button type="submit" disabled={wSaving} style={{ flex: 1, padding: "13px 0", background: C.ink, color: C.bg, border: "none", cursor: "pointer", fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2, opacity: wSaving ? .6 : 1 }}>
                  {wSaving ? "등록 중..." : "등록"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
